// WaffySSO — Keycloak OIDC auth (replaces the legacy hardcoded-admin / shared-JWT login).
//
// Flow: redirect to Keycloak (waffy-staff realm) → Authorization Code + PKCE → callback
// exchanges code for tokens → roles read from the access token's realm_access.roles claim.
// Gate: staff-ops OR staff-engineer-lead (see RequireAuth + oidc.ts ALLOWED_ROLES).
//
// The legacy AuthState shape (status + user) is preserved so existing consumers
// (Sidebar sign-out, RequireAuth) keep working with minimal churn.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { User } from "oidc-client-ts";
import { userManager, hasAllowedRole, canDeclareIncident } from "./oidc";
import { setAccessTokenGetter } from "../api/client";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "forbidden"; user: string; roles: string[] } // authenticated but lacks staff-ops / staff-engineer-lead
  | { status: "authenticated"; user: string; roles: string[] };

interface AuthCtx {
  state: AuthState;
  /** Keycloak display name when authenticated, else null. Saves consumers narrowing the union. */
  currentUser: string | null;
  /** True when the signed-in user holds a role permitted to declare incidents (see DECLARE_ROLES). */
  canDeclare: boolean;
  /** Begin the OIDC redirect login. Pass returnTo to restore a specific path after callback. */
  login: (returnTo?: string) => Promise<void>;
  /** Front-channel single logout via Keycloak end-session endpoint. */
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

/** Decode realm roles by base64-decoding the raw access token payload (realm_access.roles). */
function rolesFromAccessToken(user: User | null): string[] {
  if (!user?.access_token) return [];
  try {
    const payload = JSON.parse(atob(user.access_token.split(".")[1])) as {
      realm_access?: { roles?: string[] };
    };
    return payload.realm_access?.roles ?? [];
  } catch {
    return [];
  }
}

function displayName(user: User | null): string {
  const p = user?.profile as { preferred_username?: string; email?: string; name?: string } | undefined;
  return p?.preferred_username ?? p?.email ?? p?.name ?? "staff";
}

function stateForUser(user: User | null): AuthState {
  if (!user || user.expired) return { status: "anonymous" };
  const roles = rolesFromAccessToken(user);
  const name = displayName(user);
  return hasAllowedRole(roles)
    ? { status: "authenticated", user: name, roles }
    : { status: "forbidden", user: name, roles };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    // Give the API client a live getter for the current access token.
    setAccessTokenGetter(async () => {
      const u = await userManager.getUser();
      return u && !u.expired ? u.access_token : null;
    });

    // Boot: load any existing user from storage; the silent-renew listener keeps it fresh.
    userManager
      .getUser()
      .then((u) => setState(stateForUser(u)))
      .catch(() => setState({ status: "anonymous" }));

    const onLoaded = (u: User) => setState(stateForUser(u));
    const onUnloaded = () => setState({ status: "anonymous" });
    const onExpired = () => setState({ status: "anonymous" });
    const onSilentRenewError = (e: Error) => {
      // Silent renew failed (SSO session likely expired) → force re-auth.
      // eslint-disable-next-line no-console
      console.warn("[oidc] silent renew error:", e);
      setState({ status: "anonymous" });
    };
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addAccessTokenExpired(onExpired);
    userManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeAccessTokenExpired(onExpired);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, []);

  const login = useCallback(async (returnTo?: string) => {
    // Preserve where the user was headed so the callback can return them there.
    await userManager.signinRedirect({
      state: { returnTo: returnTo ?? window.location.pathname + window.location.search },
    });
  }, []);

  const logout = useCallback(async () => {
    // Front-channel SLO: Keycloak end-session endpoint clears the staff SSO session
    // and (per 11-session-and-logout.md) cascades logout to other staff tools.
    const user = await userManager.getUser();
    await userManager.signoutRedirect({
      id_token_hint: user?.id_token,
    });
  }, []);

  const currentUser = state.status === "authenticated" ? state.user : null;
  const canDeclare = state.status === "authenticated" && canDeclareIncident(state.roles);

  return (
    <Ctx.Provider value={{ state, currentUser, canDeclare, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
